import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, CheckCircle, Bell } from 'lucide-react';
import supabase from '../supabase/supabase';
import { envConfig } from '../constants';
import fetchWithRetry from '../utils/fetch-with-retry';

const APPSUMO_REVIEW_URL = 'https://appsumo.com/products/writevoice';

type Step = 'rating' | 'positive' | 'negative' | 'success';

interface RatingModalProps {
  isOpen: boolean;
  isAppSumo: boolean;
  onDismiss: () => void;
}

export const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  isAppSumo,
  onDismiss,
}) => {
  const [step, setStep] = useState<Step>('rating');
  const [selectedStars, setSelectedStars] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('rating');
      setSelectedStars(0);
      setHoveredStar(0);
      setFeedback('');
      setSubmitting(false);
    }
  }, [isOpen]);

  // Prevent body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Escape key to dismiss
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'success') onDismiss();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [step, onDismiss]);

  const handleStarClick = (star: number) => {
    setSelectedStars(star);
    setTimeout(() => {
      if (star >= 4) setStep('positive');
      else setStep('negative');
    }, 250);
  };

  const handleRateAction = async () => {
    if (isAppSumo) {
      window.open(APPSUMO_REVIEW_URL, '_blank', 'noopener,noreferrer');
    }
    onDismiss();
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      await fetchWithRetry(`${envConfig.EXPRESS_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({
          feedback: feedback.trim(),
          stars: selectedStars,
          priority: 'high',
          source: 'rating_modal_web',
        }),
      });
    } catch {
      // Always show success — same behaviour as mobile
    }

    setSubmitting(false);
    setStep('success');
    setTimeout(onDismiss, 2800);
  };

  const displayStars = hoveredStar || selectedStars;

  const renderContent = () => {
    switch (step) {
      case 'rating':
        return (
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              How's your experience so far?
            </h3>
            <p className="text-sm text-gray-500 mb-8">Tap a star to let us know</p>
            <div className="flex justify-center gap-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <motion.button
                  key={star}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleStarClick(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="p-1 focus:outline-none"
                >
                  <Star
                    className={`w-10 h-10 transition-colors duration-150 ${
                      displayStars >= star
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-200'
                    }`}
                    strokeWidth={1.5}
                  />
                </motion.button>
              ))}
            </div>
          </div>
        );

      case 'positive':
        return (
          <div className="text-center">
            <p className="text-xl font-semibold text-gray-900 mb-8 leading-relaxed">
              Reviews is how we survive vs the big guys, can you rate us? 💙
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRateAction}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-2xl py-4 text-base font-semibold mb-3 transition-colors"
            >
              {isAppSumo ? 'Leave a Review on AppSumo' : 'Share the love ❤️'}
            </motion.button>
            <button
              onClick={onDismiss}
              className="text-gray-400 text-sm hover:text-gray-500 transition-colors py-2"
            >
              Maybe later
            </button>
          </div>
        );

      case 'negative':
        return (
          <div>
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                What could be better?
              </h3>
              <p className="text-sm text-gray-500">Tell us and we'll fix it</p>
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What's not working for you..."
              rows={4}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-300 mb-4 transition-all"
            />
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmitFeedback}
              disabled={!feedback.trim() || submitting}
              className={`w-full rounded-2xl py-4 text-base font-semibold mb-3 flex items-center justify-center gap-2 transition-colors ${
                feedback.trim() && !submitting
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Bell className="w-4 h-4" />
              {submitting ? 'Sending...' : 'Notify Team'}
            </motion.button>
            <button
              onClick={onDismiss}
              className="w-full text-gray-400 text-sm hover:text-gray-500 transition-colors py-2"
            >
              Skip
            </button>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-green-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Message sent!
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              We sent a high priority notification to the team.
              <br />
              Thanks for taking the time — it really helps.
            </p>
          </div>
        );
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={step !== 'success' ? onDismiss : undefined}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
            style={{ margin: 0 }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed inset-0 flex items-center justify-center z-[101] p-4 pointer-events-none"
          >
            <div
              className="bg-white rounded-3xl p-8 w-full max-w-sm pointer-events-auto relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle (decorative) */}
              <div className="w-10 h-1 bg-gray-100 rounded-full mx-auto mb-6" />

              {/* Close button */}
              {step !== 'success' && (
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onDismiss}
                  className="absolute top-5 right-5 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" strokeWidth={2} />
                </motion.button>
              )}

              {renderContent()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
